/**
 * Which dialog owns the keyboard.
 *
 * Every `Modal` in this app stays mounted for the life of its host and only toggles a CSS class, so
 * "how many are open" is not something the DOM answers — several are always present and any number
 * of them can be showing. Each Modal registers here while it shows and unregisters when it hides,
 * and the last one in wins: exactly one keydown listener runs for the whole app and it only ever
 * addresses the top entry.
 *
 * Two bugs this replaces, both from the per-instance `window` listener it grew out of:
 *
 * - **Escape closed the stack.** `UpsertAppointmentModal` renders `ScheduleConflictDialogs` and
 *   `DiscardChangesDialog` as siblings, so a 409 puts two showing Modals on screen. One Escape ran
 *   both listeners: the conflict dialog *and* the form behind it closed, taking the payload the
 *   dialog was asking about with it.
 * - **Two focus traps fought.** A trap in the form modal drags focus back out of the confirm dialog
 *   layered over it. Only the top entry traps.
 */

export interface ModalStackEntry {
  /** Escape reached this modal. Only ever called on the topmost entry. */
  onEscape: () => void;
  /**
   * The dialog panel, read lazily — it is a ref at the call site and is still null on the render
   * that registers.
   */
  panel: () => HTMLElement | null;
  /**
   * Whether Tab is kept inside the panel. False for `placement="side"`, which deliberately leaves
   * the disposition board behind it reachable — trapping there would make the board unusable by
   * keyboard while the form it edits is open.
   */
  trapFocus: boolean;
}

const stack: ModalStackEntry[] = [];

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button",
  "input",
  "select",
  "textarea",
  "[tabindex]",
].join(",");

/**
 * Tab stops inside the panel, in DOM order.
 *
 * `:disabled` rather than `[disabled]` so controls inside the `fieldset disabled={isPending}` that
 * both upsert forms wrap their fields in drop out while a save is in flight — the attribute is on
 * the fieldset, not on them.
 */
function getFocusable(panel: HTMLElement): HTMLElement[] {
  return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) =>
      el.tabIndex >= 0 &&
      !el.matches(":disabled") &&
      // Cheap visibility test that still keeps the visually-hidden checkbox inputs, which do have
      // a box. `offsetParent` would be wrong here — the panel itself is inside a fixed container.
      el.getClientRects().length > 0,
  );
}

/**
 * Escape, in the **bubble** phase — deliberately, unlike the Tab trap below.
 *
 * A dropdown open inside the dialog owns Escape before the dialog does: the first press should shut
 * the option list, not throw away the form behind it. Listening on the way back up lets those
 * handlers claim the key with `stopPropagation()` and never reach here. `Select`,
 * `MultiSelectDropdown` and `EntitySearchSelect` all do exactly that while their list is open.
 */
function handleEscape(event: KeyboardEvent) {
  if (event.key !== "Escape") return;
  const top = stack[stack.length - 1];
  if (!top) return;
  top.onEscape();
}

/**
 * Tab, in the **capture** phase — the trap has to win over anything inside the panel that also
 * looks at Tab, or focus is already gone by the time it runs.
 */
function handleTab(event: KeyboardEvent) {
  if (event.key !== "Tab") return;
  const top = stack[stack.length - 1];
  if (!top || !top.trapFocus) return;

  const panel = top.panel();
  if (!panel) return;

  const focusable = getFocusable(panel);
  const active = document.activeElement as HTMLElement | null;

  // An empty panel still may not leak focus to the page behind it.
  if (focusable.length === 0) {
    event.preventDefault();
    panel.focus({ preventScroll: true });
    return;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  // Focus escaped the panel some other way (a click on the page behind a non-modal placement, a
  // programmatic focus): pull it back to the near end rather than letting Tab walk off.
  if (!active || !panel.contains(active)) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus({ preventScroll: true });
    return;
  }

  if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus({ preventScroll: true });
    return;
  }

  if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus({ preventScroll: true });
  }
}

function subscribe() {
  document.addEventListener("keydown", handleTab, true);
  document.addEventListener("keydown", handleEscape);
}

function unsubscribe() {
  document.removeEventListener("keydown", handleTab, true);
  document.removeEventListener("keydown", handleEscape);
}

/**
 * Register a showing modal. Returns the unregister function — hand it straight back from the
 * effect that opened it.
 */
export function pushModal(entry: ModalStackEntry): () => void {
  if (stack.length === 0) subscribe();
  stack.push(entry);

  return () => {
    const index = stack.lastIndexOf(entry);
    if (index !== -1) stack.splice(index, 1);
    if (stack.length === 0) unsubscribe();
  };
}

/** Test seam — the stack is module state and would otherwise leak between cases. */
export function resetModalStack(): void {
  stack.length = 0;
  unsubscribe();
}
